import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, 
  faArrowRight, 
  faTimes,
  faBuilding,
  faSchool,
  faHospital,
  faIndustry,
  faHome,
  faStore
} from '@fortawesome/free-solid-svg-icons';
import '../../styles/ReferencesPage.css';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect as useReactEffect } from 'react';
// Daten kommen jetzt aus Sanity
// sanity entfernt
// Import project images (muss vor jeglichem Code stehen)
import lessingImg from '../../assets/images/referenzen/01 Lessing/lessing.png';
import ckgImg from '../../assets/images/referenzen/02 CKG/ckg.png';
import peutingerImg from '../../assets/images/referenzen/03 Peutinger/peutinger.jpg';
import wilhelmErnstImg from '../../assets/images/referenzen/05 Wilhelm Ernst Schule/wilhelm-ernst-grundschule.png';
import schmittSohnImg from '../../assets/images/referenzen/06 Schmitt+Sonn/schmittundsohn.png';
import csgImg from '../../assets/images/referenzen/07 CSG/christoph-scheiner-gym.jpg';
import schanzImg from '../../assets/images/referenzen/08 auf der Schanz/schuleaufderschanz.jpg';
import buchdorfImg from '../../assets/images/referenzen/09 Buchdorf/buchdorf.png';
import tanzhausImg from '../../assets/images/referenzen/laufende Referenzen/tanzhaus.png';
import donBoscoImg from '../../assets/images/referenzen/laufende Referenzen/Don_Bosco.png';
import inningImg from '../../assets/images/referenzen/laufende Referenzen/Inning.png';
import affingRefImg from '../../assets/images/referenzen/Affing.png';
import caritasRefImg from '../../assets/images/referenzen/Caritas.png';
import kissingRefImg from '../../assets/images/referenzen/Kissing.png';
import babenhausenRefImg from '../../assets/images/referenzen/babenhausen.png';
import neuburgRefImg from '../../assets/images/referenzen/neuburg.png';
import noerdlingenRefImg from '../../assets/images/referenzen/Nördlingen.png';
import kitaMuensterRefImg from '../../assets/images/referenzen/Kita Münster.png';

// Hilfsfunktionen für Sanity nicht erforderlich – Upload/Editor entfernt

// weitere Projektbilder entfernt

// Referenz-Struktur
interface Reference {
  id: string;
  title: string;
  category: string;
  status: 'laufend' | 'abgeschlossen';
  image: string; // legacy image URL support
  mediaUrl?: string;
  mediaType?: 'image' | 'video';
  media?: { url: string; type: 'image' | 'video' }[]; // mehrere Dateien
  description: string;
  location: string;
  year: string;
  details: {
    client: string;
    contactPerson?: string;
    contactPhone?: string;
    services: string;
    area: string;
    budget?: string;
    duration: string;
    constructionType?: {
      imBetrieb?: boolean;
      neubau?: boolean;
      erweiterung?: boolean;
      umbau?: boolean;
    };
    servicePeriod?: string; // Leistungszeitraum
    performancePhases?: string; // LPH 1-3, 5-9
    plantGroups?: string; // Anlagengruppen
    planningFeatures?: string[]; // Planungsmerkmale
  };
}
const staticReferences: Reference[] = [
  {
    id: 'gotthold-ephraim-lessing-schule',
    title: 'Gotthold-Ephraim-Lessing-Schule',
    category: 'Öffentlich',
    status: 'abgeschlossen',
    image: lessingImg,
    description: 'Neubau einer dreigeschossigen Grundschule mit modernster technischer Infrastruktur. Abbruch des Bestandsgebäudes und Umsetzung innovativer Energielösungen.',
    location: '85049 Ingolstadt',
    year: 'ab 08/2023',
    // Anzeige auf der Karte: ab 08/2023 gewünscht
    // Das Feld year wird auf "ab 08/2023" gesetzt, damit es im Grid korrekt erscheint
    // Detailfelder bleiben zur Nachvollziehbarkeit unverändert
    // (duration/servicePeriod enthalten weiterhin den exakten Zeitraum)
    // Hinweis: Falls eine spätere Jahresgruppierung nötig ist, bitte separat lösen
    details: {
      client: '—',
      services: 'Elektroplanung',
      area: '—',
      budget: 'ca. 3,65 Mio. € (brutto)',
      duration: '12/2017 – 03/2023',
      constructionType: { neubau: true },
      servicePeriod: '12/2017 – 03/2023',
      performancePhases: 'LPH 1–3 und 5–9',
      plantGroups: 'Anlagengruppen 4, 5 und 6 (ELT)',
      planningFeatures: [
        '3D-Planung zur Kollisionsprüfung',
        'Niederspannungsschaltanlagen inkl. Leitungen und Verlegewegen',
        'Sicherheitsbeleuchtung mit Einzelbatteriesystem',
        'Beleuchtung mit Tageslichtsteuerung',
        'Fundamenterdungs- und Blitzschutzanlage',
        'Gefahrenmeldeanlagen (BMA, ELA)',
        'Informationstechnische Anlagen mit Backbone',
        'Elektrische Schließanlagen und Zutrittskontrollen',
        'E-Ladesäulen',
        'PV-Anlage mit 265 kWp',
        'Licht- und Tontechnik der Bühne',
        'Medienausstattung (PCs, Laptops, Beamer, Displays)'
      ]
    }
  },
  {
    id: 'christoph-kolumbus-grundschule',
    title: 'Christoph-Kolumbus-Grundschule',
    category: 'Öffentlich',
    status: 'abgeschlossen',
    image: ckgImg,
    description: 'Neubau eines dreigeschossigen Schulgebäudes als Erweiterung der bestehenden Grundschule. Gesamtraumprogramm mit ca. 3.990 m² Hauptnutzfläche.',
    location: '85049 Ingolstadt',
    year: '2022',
    details: {
      client: '—',
      services: 'Elektroplanung',
      area: 'ca. 3.990 m² Hauptnutzfläche',
      budget: 'ca. 1,61 Mio. € (brutto)',
      duration: '04/2018 – 09/2022',
      constructionType: { neubau: true, erweiterung: true },
      servicePeriod: '04/2018 – 09/2022',
      performancePhases: 'LPH 1–9',
      plantGroups: 'Anlagengruppen 4, 5 und 6',
      planningFeatures: [
        'Niederspannungsschaltanlagen',
        'PV-Anlage mit 60 kWp',
        'E-Ladestation und Schrankenanlage',
        'Aufzugsanlage mit vier Haltestellen (Durchlader)',
        'Sicherheitsbeleuchtung mit Einzelbatteriesystem',
        'Beleuchtung mit Tageslichtsteuerung',
        'Fundamenterdungs- und Blitzschutzanlage',
        'Gefahrenmeldeanlagen (BMA, ELA)',
        'Strukturierte Verkabelung',
        'Elektrische Schließanlagen',
        'Licht- und Tontechnik für die Bühne',
        'Medienausstattung'
      ]
    }
  },
  {
    id: 'peutinger-gymnasium',
    title: 'Peutinger Gymnasium',
    category: 'Öffentlich',
    status: 'abgeschlossen',
    image: peutingerImg,
    description: 'Umbau und Sanierung im laufenden Betrieb einer weiterführenden Schule mit Süd-, West- und Nordflügel, Turnhalle und Kollegstufe. Umfassende Brandschutzsanierung.',
    location: '86150–86199 Augsburg',
    year: '2022',
    details: {
      client: '—',
      services: 'Elektroplanung',
      area: 'BGF ca. 3.060 m²',
      budget: 'ca. 1,57 Mio. € (brutto) – AG 4+5',
      duration: '02/2018 – 07/2022',
      constructionType: { umbau: true, imBetrieb: true },
      servicePeriod: '02/2018 – 07/2022',
      performancePhases: 'LPH 2–9',
      plantGroups: 'Anlagengruppen 4 und 5',
      planningFeatures: [
        'Erweiterung und Austausch der Elektroverteilungen',
        'Erweiterung der Brandmeldeanlage',
        'Austausch und Ergänzung der Beleuchtungsanlage',
        'Erweiterung der Erdungsanlage',
        'Erweiterung der Türsteuerungen',
        'Rauch- und Wärmeabzugsanlagen',
        'Entfernung der klassischen Nullung',
        'Optimierung der Betriebskosten',
        'Zukunftsorientiertes Anlagenkonzept'
      ]
    }
  },
  {
    id: 'wohnanlage-neuburg',
    title: 'Wohnanlage Neuburg an der Donau',
    category: 'Wohnbau',
    status: 'abgeschlossen',
    image: neuburgRefImg,
    description: 'Neubau einer Wohnanlage mit 86 Wohneinheiten, Tiefgarage mit 108 Stellplätzen und Lebensmittelmarkt im Erdgeschoss.',
    location: '86633 Neuburg an der Donau',
    year: '2021',
    details: {
      client: '—',
      services: 'Elektroplanung',
      area: '86 Wohneinheiten, 108 Stellplätze',
      budget: 'ca. 1,08 Mio. € (brutto)',
      duration: '05/2018 – 10/2021',
      constructionType: { neubau: true },
      servicePeriod: '05/2018 – 10/2021',
      performancePhases: 'LPH 1–3 und 5–9',
      plantGroups: 'Anlagengruppen 4 und 5',
      planningFeatures: [
        'Niederspannungshauptverteilungen',
        'Brandmeldeanlagen',
        'Elektroakustische Lautsprecheranlagen',
        'Einbruchmeldeanlagen',
        'Videoanlage',
        'Beleuchtungsanlagen',
        'Vernetzung und Auswertung von Zählern',
        'Erdungsanlagen',
        'Außenbeleuchtungsanlage',
        'Türsteuerungen',
        'Rauch- und Wärmeabzugsanlagen'
      ]
    }
  },
  {
    id: 'wilhelm-ernst-grundschule',
    title: 'Wilhelm-Ernst-Grundschule',
    category: 'Öffentlich',
    status: 'abgeschlossen',
    image: wilhelmErnstImg,
    description: 'Umbau und Sanierung im laufenden Betrieb einer Grundschule mit modernisierter technischer Ausstattung und Integration moderner Medienkonzepte.',
    location: '85049 Ingolstadt',
    year: '2019',
    details: {
      client: '—',
      services: 'Elektroplanung',
      area: '—',
      budget: 'ca. 1,03 Mio. € (brutto) – AG 4+5',
      duration: '01/2016 – 04/2019',
      constructionType: { umbau: true, imBetrieb: true },
      servicePeriod: '01/2016 – 04/2019',
      performancePhases: 'LPH 1–9',
      plantGroups: 'Anlagengruppen 4 und 5',
      planningFeatures: [
        'Erneuerung von Unterverteilungen',
        'Kompensation der klassischen Nullung',
        'Erneuerung der Netzstruktur',
        'Küchenplanung',
        'Beleuchtungsanlagen und Rauchwarnmelder',
        'Neuer Backbone',
        'Strukturierte Verkabelung',
        'Multimediaausstattung für interaktive Whiteboards',
        'Integration von Dokumentenkameras'
      ]
    }
  },
  {
    id: 'schmitt-sohn-verwaltung',
    title: 'Neubau Verwaltung Schmitt + Sohn',
    category: 'Gewerbe',
    status: 'abgeschlossen',
    image: schmittSohnImg,
    description: 'Neubau eines modernen Verwaltungsgebäudes mit Energiemonitoring-System über Symcom zur zentralen Analyse und Steuerung des Energieverbrauchs.',
    location: '85716 Unterschleißheim',
    year: '2022',
    details: {
      client: 'Schmitt + Sohn',
      services: 'Elektroplanung',
      area: '—',
      budget: 'ca. 0,88 Mio. € (netto)',
      duration: '04/2018 – 09/2022',
      constructionType: { neubau: true },
      servicePeriod: '04/2018 – 09/2022',
      performancePhases: 'LPH 1–3 und 5–9',
      plantGroups: 'Anlagengruppen 4 und 5',
      planningFeatures: [
        'Niederspannungsschaltanlagen',
        'PV-Anlage mit 43 kWp',
        'E-Ladestation und Schrankenanlage',
        'Sicherheitsbeleuchtung mit Einzelbatteriesystem',
        'Beleuchtung mit Tageslichtsteuerung',
        'Fundamenterdungs- und Blitzschutzanlage',
        'Gefahrenmeldeanlagen (BMA, ELA)',
        'Strukturierte Verkabelung',
        'Elektrische Schließanlagen',
        'Medienausstattung',
        'Jalousiesteuerung über Bussystem',
        'Energiemonitoring-System (Symcom)'
      ]
    }
  },
  {
    id: 'christoph-scheiner-gymnasium',
    title: 'Christoph-Scheiner-Gymnasium',
    category: 'Öffentlich',
    status: 'abgeschlossen',
    image: csgImg,
    description: 'Umbau und Sanierung im laufenden Betrieb einer weiterführenden Schule in denkmalgeschütztem Gebäude. Aufteilung in vier Bauabschnitte.',
    location: '85049 Ingolstadt',
    year: '2019',
    details: {
      client: '—',
      services: 'Elektroplanung',
      area: '—',
        budget: '726.069,46 € (AG 4+5)',
      duration: '07/2014 – 12/2019',
      constructionType: { umbau: true, imBetrieb: true },
      servicePeriod: '07/2014 – 12/2019',
      performancePhases: 'LPH 1–9',
      plantGroups: 'Anlagengruppen 4 und 5',
      planningFeatures: [
        'Erneuerung der Niederspannungshauptanlage',
        'Austausch der Unterverteilungen',
        'Neuer Backbone',
        'Erweiterung der strukturierten Verkabelung',
        'Brandmeldeanlage mit Aufschaltung',
        'Angebotseinholung Nebengewerke',
        'Überwachung der Mängelbeseitigung',
        'Denkmalschutz'
      ]
    }
  },
  {
    id: 'schule-auf-der-schanz',
    title: 'Schule auf der Schanz',
    category: 'Öffentlich',
    status: 'abgeschlossen',
    image: schanzImg,
    description: 'Umbau und Arbeiten im laufenden Betrieb einer Grund- und Mittelschule. Umsetzung in drei Bauabschnitten mit Sanierung der Elektroinstallation und Datentechnik.',
    location: '85049 Ingolstadt',
    year: '2017',
    details: {
      client: '—',
      services: 'Elektroplanung',
      area: '—',
      budget: '726.069,46 € (AG 4+5)',
      duration: '05/2013 – 07/2017',
      constructionType: { umbau: true, imBetrieb: true },
      servicePeriod: '05/2013 – 07/2017',
      performancePhases: 'LPH 1–9',
      plantGroups: 'Anlagengruppen 4 und 5',
      planningFeatures: [
        'Erneuerung der Niederspannungshauptanlage',
        'Erneuerung der Unterverteilungen',
        'Kompensation der klassischen Nullung',
        'Erneuerung der Netzstruktur',
        'Neuer Backbone',
        'Strukturierte Verkabelung',
        'Vorbereitung für interaktive Whiteboards',
        'Drei Bauabschnitte'
      ]
    }
  },
  {
    id: 'buergerzentrum-buchdorf',
    title: 'Bürgerzentrum Buchdorf – Rathaus',
    category: 'Öffentlich',
    status: 'abgeschlossen',
    image: buchdorfImg,
    description: 'Neubau eines Rathauses auf vier Geschossen mit barrierefreier und behindertengerechter Elektroplanung sowie Kollisionsprüfung zwischen HLS und ELT.',
    location: '86675 Buchdorf',
    year: '2021',
    details: {
      client: '—',
      services: 'Elektroplanung',
      area: 'Vier Geschosse',
      budget: 'ca. 0,46 Mio. € (brutto) – AG 4+5+6',
      duration: '07/2018 – 07/2021',
      constructionType: { neubau: true },
      servicePeriod: '07/2018 – 07/2021',
      performancePhases: 'LPH 1–8',
      plantGroups: 'Anlagengruppen 4, 5 und 6',
      planningFeatures: [
        'Aufzugsanlage mit vier Haltestellen',
        'Barrierefreie und behindertengerechte Planung',
        'Kollisionsprüfung HLS und ELT',
        'Niederspannungsinstallationsanlage',
        'Bürobeleuchtung mit Tageslichtsteuerung (Bussystem)',
        'Fundamenterdungsanlage',
        'Strukturierte Verkabelung für Internet und Telefonie',
        'Sprechanlage'
      ]
    }
  },
  {
    id: 'kita-muenster',
    title: 'Kindertagesstätte Münster',
    category: 'Öffentlich',
    status: 'abgeschlossen',
    image: kitaMuensterRefImg,
    description: 'Neubau einer Kindertagesstätte mit Kindergarten und Krippe.',
    location: '86692 Münster',
    year: '2019',
    details: {
      client: '—',
      services: 'LPH 1–9 (nach HOAI 2013)',
      area: '—',
      budget: 'ca. 0,14 Mio. € (brutto) – AG 4+5',
      duration: 'Fertigstellung: 05/2019',
      constructionType: { neubau: true },
      servicePeriod: '04/2016 – 05/2017',
      performancePhases: 'LPH 1–9',
      plantGroups: '—',
      planningFeatures: [
        'Elektroinstallation',
        'Beleuchtungsanlage',
        'Hausalarmanlage',
        'Steuerung über Bussystem',
        'Blitzschutz- und Erdungsanlage',
        'Videosprechanlage'
      ]
    }
  },
  {
    id: 'tanzhaus-donauwoerth',
    title: 'Tanzhaus Donauwörth',
    category: 'Kultur',
    status: 'laufend',
    image: tanzhausImg,
    media: [
      { url: 'https://drive.google.com/file/d/1f98IQvuIiabWNA-qLKBIcxkeLfUlv6RD/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/19AprrWf-NBetIFbkfU3c9hofq6NxX14N/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1u0KEjTIe9wcgCJn0PwOXXNl8ZEFqgjHy/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/192dwDpo7m1k12uf4a8U_1UDrH-wy5y9R/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1dohY5mXJM69OJ7tJD-IihOLJsuNKmQR2/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/18A6enIqgCAVbmmvtxJFqDFgCaddKI4Ct/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1aJqBFhz4YjXqEPM2K7T2oE-g06aVU8zs/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/14FPWwKmAEg9haWBBvxQ1UhRIYwKlUguJ/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1FWuqAY3nq-yHrDnZZ6N4CHbQ5saJZMfF/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1g6HfUKZhwpgQy1i2FrkV1P8cAAzptP9J/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1vZiO_JoALulITGerH90XLrFEocr0r9MJ/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1Wj5Wndjvm0zW0MG3gmaZXIjc0yls9p1h/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1Pu7JY_p6EK4LhIBMxdUCPI7qtoxuIyw0/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/12Qd3oJzFpI-hRahPxJIAlyXmZgGlLimA/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1yQ_X7FZes0f6LqiCYACYAs09yFNzyyBc/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1viPUf2s_0rLRn3spZ8c-VdkzaKMlbbwK/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1_ezOUM7W3z05wNsm9fw_RK2qjubwptO1/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1Ck0b2mbAH0ea9d_bacCxhSXOC-kDlRJt/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1Gqe98HEW4I7zRsV7btT3ZI1oL2MwIZaE/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1lfRP428QSOo45fcyhojKgODpU_k8JpOS/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1kmxMeTIzEXpNnEbhCwH8cxlsexG1CdTb/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/12Au454Wj9sHS96C3OjtpShfh7ZKqNFXV/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1qrlLMcqqN7ndMKqIojYT1L_7sMMp5P-y/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1dx_bLbRHkLrYqwFnRNCj_ZljuSnlOneR/view?usp=drive_link', type: 'image' }
    ],
    description: 'Die Stadt Donauwörth hat beschlossen, ein jahrhundertealtes Gebäude im zentralen Teil der Stadt zu sanieren. 2 Tiefgaragen, Gemeinschaftsräume, Cafés, Bibliothek, Büroräume, ein Hochzeitsaal und vieles mehr werden mit einem modernen KNX-System und dekorativer Beleuchtung ausgestattet.',
    location: '86609 Donauwörth',
    year: '2024',
    details: {
      client: 'Stadt Donauwörth',
      services: 'Elektroplanung, KNX-System, Beleuchtungsplanung',
      area: '—',
      budget: 'ca. 3,30 Mio. € (netto)',
      duration: '2024-2027',
      constructionType: { umbau: true },
      servicePeriod: '2024 – 2027',
      performancePhases: 'LPH 1-3 und 5-9',
      plantGroups: 'ELT',
      planningFeatures: [
        'Modernes KNX-System',
        'Dekorative Beleuchtung',
        'Tiefgaragen-Elektrifizierung',
        'Multimedia-Ausstattung',
        'Sicherheitstechnik'
      ]
    }
  },
  {
    id: 'wirtschaftsschule-noerdlingen',
    title: 'Wirtschaftsschule Nördlingen',
    category: 'Öffentlich',
    status: 'laufend',
    image: noerdlingenRefImg,
    description: 'Für die Sanierung und Umbau der Wirtschaftsschule in Nördlingen ist nicht nur ein kompletter Austausch der Elektroinstallation vorgesehen, sondern auch die Ausstattung der Klassenräume mit moderner Multimedia-Technik.',
    location: '86720 Nördlingen',
    year: '2024',
    details: {
      client: '—',
      services: 'Elektroplanung, Multimedia-Ausstattung',
      area: '—',
      budget: 'ca. 1,30 Mio. € (netto)',
      duration: '2024-2027',
      constructionType: { umbau: true },
      servicePeriod: '2024 – 2027',
      performancePhases: 'LPH 1-3 und 5-9',
      plantGroups: 'ELT',
      planningFeatures: [
        'Kompletter Austausch der Elektroinstallation',
        'Moderne Multimedia-Ausstattung für Klassenräume',
        'Netzwerktechnik',
        'Beleuchtungsplanung'
      ]
    }
  },
  {
    id: 'grundschule-kissing',
    title: 'Grundschule Kissing',
    category: 'Öffentlich',
    status: 'laufend',
    image: kissingRefImg,
    description: 'Um dem steigenden Bedarf an Schulflächen entgegenzuwirken, hat die Gemeinde Kissing das Projekt Neubau Grundschule ins Leben gerufen. Holzarchitektur mit dem Low-Tech-Konzept soll sowohl einen praktischen als auch einen optischen Wert haben.',
    location: '86438 Kissing',
    year: '2024',
    details: {
      client: 'Gemeinde Kissing',
      services: 'Elektroplanung',
      area: '—',
      budget: 'ca. 1,40 Mio. € (netto)',
      duration: '—',
      constructionType: { neubau: true },
      servicePeriod: '—',
      performancePhases: 'LPH 1-3 und 5-9',
      plantGroups: 'ELT',
      planningFeatures: [
        'Low-Tech-Konzept',
        'Elektroinstallation für Holzarchitektur',
        'Energieeffiziente Beleuchtung',
        'Moderne Medientechnik'
      ]
    }
  },
  {
    id: 'schulerweiterung-inning',
    title: 'Schulerweiterung Inning am Ammersee',
    category: 'Öffentlich',
    status: 'laufend',
    image: inningImg,
    description: 'Um neue Unterrichts- und Integrationsräume zu schaffen, hat sich die Gemeinde Inning am Ammersee für ein Projekt zur Erweiterung des bestehenden Gebäudes um eine Fläche von ca. 2.000m² entschieden.',
    location: '82266 Inning am Ammersee',
    year: '2024',
    details: {
      client: 'Gemeinde Inning am Ammersee',
      services: 'Elektroplanung',
      area: 'ca. 2.000 m²',
      budget: 'ca. 1,10 Mio. € (netto)',
      duration: '—',
      constructionType: { erweiterung: true },
      servicePeriod: '—',
      performancePhases: 'LPH 2-9',
      plantGroups: 'ELT',
      planningFeatures: [
        'Elektroinstallation',
        'Multimedia-Ausstattung',
        'Beleuchtungsplanung'
      ]
    }
  },
  {
    id: 'don-bosco-foerderschule-rottenbuch',
    title: 'Don-Bosco-Förderschule Rottenbuch',
    category: 'Öffentlich',
    status: 'laufend',
    image: donBoscoImg,
    description: 'Seit 2010 werden verschiedene historische und neuzeitliche Gebäude von Regens Wagner sukzessive saniert und umstrukturiert. Der Neubau der Don-Bosco-Förderschule ist nach der aktuell abgeschlossenen Sanierung der Berufsfachschule und Fachakademie in Rottenbuch der nächste große Schritt.',
    location: '82401 Rottenbuch',
    year: '2024',
    details: {
      client: 'Regens Wagner',
      services: 'Elektroplanung',
      area: 'BGF ca. 5.705 m²',
      budget: 'ca. 3,00 Mio. € (netto)',
      duration: '—',
      constructionType: { neubau: true, umbau: true },
      servicePeriod: '—',
      performancePhases: 'LPH 1-3 und 5-9',
      plantGroups: 'ELT',
      planningFeatures: [
        'Abbruch der bestehenden Förderschule (BGF ca. 2.944 m²)',
        'Neubau mit BGF ca. 5.705 m²',
        'Provisorium aus mobilen Raumeinheiten (BGF ca. 2.474 m²)',
        'Schwimmbadsanierung (BGF ca. 770 m²)',
        'Neuer Eingangsbereich zum Schwimmbad (BGF ca. 80 m²)'
      ]
    }
  },
  {
    id: 'caritas-wohngebaeude-eichstaett',
    title: 'Caritas Wohngebäude Ingolstadt',
    category: 'Wohnbau',
    status: 'laufend',
    image: caritasRefImg,
    description: 'Das über 60 Jahre alte Wohngebäude entsprach bei weitem nicht mehr den energetischen Standards und Wohnbedingungen, weshalb sich der Eigentümer, der Caritasverband, für die Sanierung und Aufstockung entschied.',
    location: '85049 Ingolstadt',
    year: '2024',
    details: {
      client: 'Caritasverband Ingolstadt',
      services: 'Elektroplanung',
      area: '—',
      budget: 'ca. 1,00 Mio. € (netto)',
      duration: '—',
      constructionType: { umbau: true },
      servicePeriod: '—',
      performancePhases: 'LPH 1-3 und 5-9',
      plantGroups: 'ELT',
      planningFeatures: [
        'Energetische Sanierung',
        'Aufstockung',
        'Moderne Elektroinstallation',
        'Wohnraumqualität'
      ]
    }
  },
  {
    id: 'zehenstadel-babenhausen',
    title: 'Zehenstadel Babenhausen',
    category: 'Kultur',
    status: 'laufend',
    image: babenhausenRefImg,
    media: [
      { url: 'https://drive.google.com/file/d/1eI0OdHLha-RZ337LGXSgrXqopixGle9e/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1-M8BQHwLjuATWh6Mzvg1lLziTbZEJAvs/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1DS6-5Pvqg2iNg_5CgZ_qUAwdwCybrbtz/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1NWxB8GcXBdbAIOeduF_l4hPJRIpJVY7m/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/11Qz438oQQFj73veLaCacNnJl-O_4P9Yd/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1RnhAC8OcACiDMCBzDOm3jc3ztgCmsCJP/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1YFS-0h6nApiKfLU_vh6FfzsLdHOifBNk/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1b7MwEJYbi3I3zkrBHXN0JjN0i1lyIZ-e/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1sPIYSZaHON44c74ACfqYFN7GxRZc24I-/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/10RrtgMONnB76q751pCcCZxzqAzRXXxlJ/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/13jizEtxnRdu4GkSje9RvQr2VsS4yQ6Yh/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1TkZemSXNXCwIyiJegCo0fMCIk8eoZy6b/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1AUbF3ZIGsTnzEXcl5TN5LOKcUW80VDXz/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1MLPqsQJpwPTd8H23zT6Jb8rSYEtMKwAT/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/13Waj5FsXY2aj5ncw8_Df37DmtgrFp3Mu/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1iPpIdG263RxqdPagQmaD7k682QIUI-Cj/view?usp=drive_link', type: 'image' },
      { url: 'https://drive.google.com/file/d/1xfhjPj-dKdwmKNmENl61h7GzuJxMNbza/view?usp=drive_link', type: 'image' }
    ],
    description: 'Endlich ist es Zeit für die Renovierung des Zehenstadels aus dem Jahr 1860 in Babenhausen. Der Umfang des Projekts umfasst die Schaffung eines Mehrzweckgebäudes, dass den Bewohnern der Gemeinde dienen soll.',
    location: '87727 Babenhausen',
    year: '2024',
    details: {
      client: 'Gemeinde Babenhausen',
      services: 'Elektroplanung',
      area: '—',
      budget: 'ca. 0,60 Mio. € (netto)',
      duration: '—',
      constructionType: { umbau: true },
      servicePeriod: '—',
      performancePhases: 'LPH 1-9',
      plantGroups: 'ELT',
      planningFeatures: [
        'Denkmalschutz',
        'Mehrzweckgebäude',
        'Historisches Gebäude von 1860',
        'Moderne Elektrotechnik'
      ]
    }
  },
  {
    id: 'schule-affing',
    title: 'Schule Affing',
    category: 'Öffentlich',
    status: 'laufend',
    image: affingRefImg,
    description: 'Das lukrative Projekt der Gemeinde Affing, das auf der Schaffung einer modernen Schule mit großem Augenmerk auf den visuellen Aspekt basiert, was durch den separat beauftragten Lichtplaner bestätigt werden kann.',
    location: '86444 Affing',
    year: '2024',
    details: {
      client: 'Gemeinde Affing',
      services: 'Elektroplanung, Lichtplanung',
      area: '—',
      budget: 'ca. 1,40 Mio. € (netto)',
      duration: '2024-2026',
      constructionType: { neubau: true },
      servicePeriod: '2024 – 2026',
      performancePhases: 'LPH 5-9',
      plantGroups: 'ELT',
      planningFeatures: [
        'Moderne Lichtplanung',
        'Visuelles Konzept',
        'Zusammenarbeit mit Lichtplaner',
        'Energieeffiziente Beleuchtung'
      ]
    }
  }
];
// Statische Seeds entfernt – Anzeige basiert ausschließlich auf Firestore

const ReferenzenPage: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<string>('Alle');
  const [activeStatus, setActiveStatus] = useState<'alle' | 'laufend' | 'abgeschlossen'>('alle');
  const [selectedReference, setSelectedReference] = useState<Reference | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState<number>(0);
  const [isZoomOpen, setIsZoomOpen] = useState<boolean>(false);
  const [zoomUrl, setZoomUrl] = useState<string>('');
  const items = staticReferences;
  
  // Normalisiert Google-Drive-Links zu direkt einbindbaren URLs
  // - Für Bilder: Thumbnail-Endpunkt (kein Cookie/Token nötig)
  // - Für Videos: uc?export=download
  const normalizeDriveUrl = (url?: string, kind: 'image' | 'video' = 'image'): string => {
    if (!url) return '';
    try {
      if (url.includes('drive.google.com/file/d/')) {
        const match = url.match(/file\/d\/([^/]+)/);
        const id = match && match[1] ? match[1] : null;
        if (id) {
          return kind === 'image'
            ? `https://drive.google.com/thumbnail?id=${id}&sz=w2000`
            : `https://drive.google.com/uc?export=download&id=${id}`;
        }
      }
      if (url.includes('drive.google.com/open')) {
        const id = new URL(url).searchParams.get('id');
        if (id) {
          return kind === 'image'
            ? `https://drive.google.com/thumbnail?id=${id}&sz=w2000`
            : `https://drive.google.com/uc?export=download&id=${id}`;
        }
      }
      if (url.includes('drive.google.com/uc?')) {
        const u = new URL(url);
        const id = u.searchParams.get('id') || '';
        if (id && kind === 'image') {
          return `https://drive.google.com/thumbnail?id=${id}&sz=w2000`;
        }
        if (id && kind === 'video') {
          u.searchParams.set('export', 'download');
          return u.toString();
        }
        return url;
      }
    } catch {}
    return url;
  };
  

  // keine Datenladung nötig – statisch gesetzt
  
  // Spezieller Anzeige-Text für die Jahresangabe in der Kartenansicht
  const getCardYear = (ref: Reference) => {
    if (ref.id === 'gotthold-ephraim-lessing-schule') {
      return '2023';
    }
    return ref.year;
  };
  
  // Get unique categories for filter
  const categoriesSet = new Set(items.map(ref => ref.category));
  const categories = ['Alle', ...Array.from(categoriesSet)];
  
  // Filter references based on active filter
  const filteredReferences = useMemo(() => {
    let base = items;
    if (activeFilter !== 'Alle') {
      base = base.filter((r) => r.category === activeFilter);
    }
    if (activeStatus !== 'alle') {
      base = base.filter((r) => r.status === activeStatus);
    }
    return base;
  }, [items, activeFilter, activeStatus]);
  
  // Handle opening the modal with selected reference
  const openModal = (reference: Reference) => {
    setSelectedReference(reference);
    setModalOpen(true);
    setCurrentMediaIndex(0);
    document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
  };
  
  // Handle closing the modal
  const closeModal = () => {
    setModalOpen(false);
    document.body.style.overflow = 'auto'; // Re-enable scrolling
  };
  
  // ESC zum Schließen des Bild-Zooms
  useReactEffect(() => {
    if (!isZoomOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsZoomOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isZoomOpen]);
  
  // Body-Scroll sperren, wenn Zoom offen ist
  useReactEffect(() => {
    if (isZoomOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isZoomOpen]);
  
  // Editor und Mutationen werden nicht mehr angeboten
  
  // Get icon for category
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Gewerbe':
        return faBuilding;
      case 'Öffentlich':
        return faSchool;
      case 'Wohnbau':
        return faHome;
      case 'Kultur':
        return faStore;
      case 'Industrie':
        return faIndustry;
      case 'Gesundheit':
        return faHospital;
      case 'Infrastruktur':
        return faBuilding;
      default:
        return faBuilding;
    }
  };
  
  useReactEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Löschen-Funktion entfernt (kein Login erforderlich / keine Admin-Aktion)

  return (
    <div className="references-page">
      {/* Header Section */}
      <header className="references-header">
        <div className="container">
          <h1>Referenzen</h1>
          <p>
            Entdecken Sie unsere erfolgreich abgeschlossenen Projekte aus verschiedenen Bereichen der Elektroplanung
          </p>
          {/* Admin-Aktionen entfernt */}
        </div>
      </header>
      
      <div className="references-content">
        <div className="container">
          {/* Filter Section */}
          <div className="references-filter">
            <h2 className="filter-title">Nach Kategorie filtern</h2>
            <div className="filter-buttons">
              {categories.map((category, index) => (
                <button
                  key={index}
                  className={`filter-button ${activeFilter === category ? 'active' : ''}`}
                  onClick={() => setActiveFilter(category)}
                >
                  {category === 'Alle' ? category : (
                    <>
                      <FontAwesomeIcon icon={getCategoryIcon(category)} className="mr-2" /> {category}
                    </>
                  )}
                </button>
              ))}
            </div>
            <div className="filter-buttons" style={{ marginTop: 12 }}>
              {['alle','laufend','abgeschlossen'].map((st) => (
                <button key={st} className={`filter-button ${activeStatus === st ? 'active' : ''}`} onClick={() => setActiveStatus(st as any)}>
                  {st === 'alle' ? 'Status: Alle' : st === 'laufend' ? 'Laufende' : 'Abgeschlossene'}
                </button>
              ))}
            </div>
          </div>
          
          {/* References Grid */}
          <AnimatePresence mode="popLayout">
            <div className="references-grid">
              {filteredReferences.map(reference => (
                <motion.div 
                  layout 
                  key={reference.id} 
                  className="reference-card" 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  exit={{ opacity: 0, y: -20 }}
                  onClick={() => openModal(reference)}
                  style={{ cursor: 'pointer' }}
                >
                  {reference.image ? (
                    <img src={normalizeDriveUrl(reference.image, 'image')} alt={reference.title} className="reference-image" />
                  ) : reference.media && reference.media.length > 0 ? (
                    reference.media[0].type === 'video' ? (
                      <video controls className="reference-image">
                        <source src={normalizeDriveUrl(reference.media[0].url, 'video')} />
                      </video>
                    ) : (
                      <img src={normalizeDriveUrl(reference.media[0].url, 'image')} alt={reference.title} className="reference-image" />
                    )
                  ) : reference.mediaType === 'video' && reference.mediaUrl ? (
                    <video controls className="reference-image">
                      <source src={normalizeDriveUrl(reference.mediaUrl, 'video')} />
                    </video>
                  ) : (
                    <div className="reference-image-placeholder" style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      textAlign: 'center',
                      padding: '20px'
                    }}>
                      Bild folgt in Kürze
                    </div>
                  )}
                  <div className="reference-content">
                    <h3 className="reference-title">{reference.title}</h3>
                    <span className="reference-category">
                      <FontAwesomeIcon icon={getCategoryIcon(reference.category)} className="mr-1" /> {reference.category} • {reference.status === 'laufend' ? 'Laufend' : 'Abgeschlossen'}
                    </span>
                    <p className="reference-text">
                      {reference.description?.length > 100 
                        ? `${reference.description.substring(0, 100)}...` 
                        : reference.description}
                    </p>
                    <div className="reference-details">
                      <div>
                        <div className="reference-location">
                          <FontAwesomeIcon icon={faMapMarkerAlt} /> {reference.location}
                        </div>
                        <div className="reference-year">{getCardYear(reference)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button 
                          className="reference-link"
                          onClick={() => openModal(reference)}
                        >
                          Details <FontAwesomeIcon icon={faArrowRight} />
                        </button>
                        {/* Admin-Löschen entfernt */}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </AnimatePresence>
          {/* CMS-gesteuert: Hinzufügen erfolgt im Sanity Studio */}
          
          {/* No Results Message */}
          {filteredReferences.length === 0 && (
            <div className="text-center py-5">
              <h3>Keine Referenzen in dieser Kategorie gefunden.</h3>
              <p>Bitte wählen Sie eine andere Kategorie oder zeigen Sie alle Referenzen an.</p>
              <button 
                className="btn btn-primary mt-3"
                onClick={() => setActiveFilter('Alle')}
              >
                Alle Referenzen anzeigen
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Erstellen-Modal entfernt */}
      {/* <div className={`reference-modal ${isAddOpen ? 'open' : ''}`} onClick={() => setIsAddOpen(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setIsAddOpen(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <div className="modal-header">
            <h2 className="modal-title">Neue Referenz anlegen</h2>
          </div>
          <div className="modal-body">
            <form className="admin-form" onSubmit={async (e) => {
                e.preventDefault();
                setSubmitError(null);
                setIsSubmitting(true);
                try {
                  const bsApiKey = process.env.REACT_APP_BYTESCALE_PUBLIC_KEY || 'public_G22nhyM7AthAz36W5VvSiGVBrDaa';
                  const uploadManager = new Bytescale.UploadManager({ apiKey: bsApiKey });
                  const uploadedMedia: { url: string; type: 'image' | 'video' }[] = [];
                  if (selectedFiles.length > 0) {
                    let totalBytes = 0;
                    let sentBytes = 0;
                    const prepared: { file: Blob | File; type: 'image' | 'video' }[] = [];
                    for (const f of selectedFiles) {
                      const ext = f.name.split('.').pop()?.toLowerCase();
                      const isVideo = ['mp4','mov','webm','m4v','avi'].includes(ext || '');
                      const type: 'image' | 'video' = isVideo ? 'video' : 'image';
                      const data = isVideo ? f : await compressImageIfNeeded(f);
                      prepared.push({ file: data, type });
                      totalBytes += (data as any).size ?? f.size;
                    }
                    for (const p of prepared) {
                      const res = await uploadManager.upload({ data: p.file as any, onProgress: ({ bytesSent, bytesTotal }) => {
                        // Aggregierter Fortschritt
                        setUploadProgress(Math.round(((sentBytes + bytesSent) / totalBytes) * 100));
                      }});
                      sentBytes += (p.file as any).size ?? 0;
                      uploadedMedia.push({ url: res.fileUrl, type: p.type });
                    }
                  }

                  const writeData = {
                    title: form.title,
                    category: form.category,
                    status: form.status,
                    image: uploadedMedia[0]?.type === 'image' && uploadedMedia[0]?.url ? uploadedMedia[0].url : form.image,
                    mediaUrl: uploadedMedia[0]?.url || null,
                    mediaType: uploadedMedia[0]?.type,
                    media: uploadedMedia.length ? uploadedMedia : null,
                    description: form.description,
                    location: form.location,
                    year: form.year,
                    client: form.details_client,
                    contactPerson: form.details_contactPerson,
                    contactPhone: form.details_contactPhone,
                    services: form.details_services,
                    area: form.details_area,
                    budget: form.details_budget || null,
                    duration: form.details_duration,
                    constructionType: {
                      imBetrieb: form.details_constructionType_imBetrieb,
                      neubau: form.details_constructionType_neubau,
                      erweiterung: form.details_constructionType_erweiterung,
                      umbau: form.details_constructionType_umbau
                    },
                    servicePeriod: form.details_servicePeriod,
                    performancePhases: form.details_performancePhases,
                    plantGroups: form.details_plantGroups,
                    planningFeatures: form.details_planningFeatures
                      ? form.details_planningFeatures.split('\n').map((s) => s.trim()).filter(Boolean)
                      : [],
                    createdAt: new Date()
                  };
                  const cleaned = sanitizeForFirestore(writeData);
                  // Debug: Payload prüfen
                  findBadValues(cleaned);
                  try { console.log('payload', JSON.parse(JSON.stringify(cleaned))); } catch {}
                  let docRef: { id: string } | null = null;
                  try {
                    // Primär: Lite (REST) – robuster gegenüber Proxys
                    const dbLite = getFirestoreLite(app as any);
                    const col = collectionLite(dbLite, 'references');
                    docRef = await addDocLite(col, cleaned);
                  } catch (e) {
                    // Fallback: normales SDK
                    const ref = await addDoc(collection(db, 'references'), cleaned);
                    docRef = { id: ref.id };
                  }
                  setIsAddOpen(false);
                  setSelectedFiles([]);
                  setUploadProgress(0);
                  setForm({
                    title: '', category: 'Öffentlich', status: 'abgeschlossen', image: '', description: '', location: '', year: '', details_client: '', details_contactPerson: '', details_contactPhone: '', details_services: '', details_area: '', details_budget: '', details_duration: '', details_constructionType_imBetrieb: false, details_constructionType_neubau: false, details_constructionType_erweiterung: false, details_constructionType_umbau: false, details_servicePeriod: '', details_performancePhases: '', details_plantGroups: '', details_planningFeatures: ''
                  });
                  // Optimistic UI: neue Referenz sofort im State anzeigen
                  if (!docRef) { throw new Error('Konnte Dokument-ID nicht bestimmen'); }
                  const newId = docRef.id;
                  setItems((prev) => [
                    {
                      id: newId,
                      title: writeData.title,
                      category: writeData.category,
                      status: writeData.status as any,
                      image: (writeData.mediaType === 'image' && writeData.mediaUrl) ? (writeData.mediaUrl as string) : (writeData.image as string),
                      mediaUrl: (writeData.mediaUrl ?? undefined) as string | undefined,
                      mediaType: writeData.mediaType as any,
                      media: (writeData.media as any) || undefined,
                      description: writeData.description,
                      location: writeData.location,
                      year: String(writeData.year),
                      details: {
                        client: writeData.client,
                        contactPerson: writeData.contactPerson,
                        contactPhone: writeData.contactPhone,
                        services: writeData.services,
                        area: writeData.area,
                        budget: writeData.budget || undefined,
                        duration: writeData.duration,
                        constructionType: writeData.constructionType as any,
                        servicePeriod: writeData.servicePeriod as any,
                        performancePhases: writeData.performancePhases as any,
                        plantGroups: writeData.plantGroups as any,
                        planningFeatures: writeData.planningFeatures as any
                      }
                    },
                    ...prev
                  ]);
                  // Hintergrund-Refresh ohne die UI zu blockieren
                  (async () => {
                    try {
                      const q = query(collection(db, 'references'), orderBy('year', 'desc'));
                      const snap = await getDocs(q);
                      const list: Reference[] = snap.docs.map((d) => {
                        const data = d.data() as any;
                        return {
                          id: d.id,
                          title: data.title || 'Ohne Titel',
                          category: data.category || 'Öffentlich',
                          status: (data.status as any) || 'abgeschlossen',
                          image: data.image || data.mediaUrl || lessingImg,
                          mediaUrl: data.mediaUrl || data.image || undefined,
                          mediaType: data.mediaType || (data.image ? 'image' : undefined),
                          description: data.description || '',
                          location: data.location || '',
                          year: String(data.year || ''),
                          details: {
                            client: data.client || '',
                            contactPerson: data.contactPerson || '',
                            contactPhone: data.contactPhone || '',
                            services: data.services || '',
                            area: data.area || '',
                            budget: data.budget || undefined,
                            duration: data.duration || '',
                            constructionType: data.constructionType || {},
                            servicePeriod: data.servicePeriod || '',
                            performancePhases: data.performancePhases || '',
                            plantGroups: data.plantGroups || '',
                            planningFeatures: Array.isArray(data.planningFeatures) ? data.planningFeatures : []
                          }
                        };
                      });
                      setItems(list);
                    } catch {}
                  })();
                } catch (err: any) {
                  setSubmitError(err?.message || 'Konnte Referenz nicht speichern');
                  // eslint-disable-next-line no-alert
                  alert(err?.message || 'Konnte Referenz nicht speichern');
                } finally {
                  setIsSubmitting(false);
                }
              }}>
                <div className="admin-form form-row">
                  <label>
                    Titel
                    <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                  </label>
                  <label>
                    Ort
                    <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required />
                  </label>
                  <label>
                    Jahr
                    <input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} required />
                  </label>
                  <label>
                    Kategorie
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                      {['Öffentlich','Gewerbe','Wohnbau','Kultur','Industrie','Gesundheit','Infrastruktur'].map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Status
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                      <option value="laufend">laufend</option>
                      <option value="abgeschlossen">abgeschlossen</option>
                    </select>
                  </label>
                </div>
                <div className="admin-form form-row">
                  <label>
                    Bauart
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={form.details_constructionType_imBetrieb} onChange={(e) => setForm({ ...form, details_constructionType_imBetrieb: e.target.checked })} />
                        Bauen im laufenden Betrieb
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={form.details_constructionType_neubau} onChange={(e) => setForm({ ...form, details_constructionType_neubau: e.target.checked })} />
                        Neubau
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={form.details_constructionType_erweiterung} onChange={(e) => setForm({ ...form, details_constructionType_erweiterung: e.target.checked })} />
                        Erweiterungsbau
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={form.details_constructionType_umbau} onChange={(e) => setForm({ ...form, details_constructionType_umbau: e.target.checked })} />
                        Umbau
                      </label>
                    </div>
                  </label>
                </div>
                <label>
                  Beschreibung
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </label>
                <div className="admin-form form-row">
                  <label>
                    Bild-URL (optional)
                    <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
                  </label>
                  <label>
                    Dateien (Bilder oder Videos)
                    <input multiple type="file" accept="image/*,video/*" onChange={(e) => setSelectedFiles(e.target.files ? Array.from(e.target.files) : [])} />
                    {(selectedFiles.length > 0) && (
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{selectedFiles.length} Datei(en) ausgewählt{uploadProgress>0 && uploadProgress<100 ? ` • Upload ${uploadProgress}%` : ''}</div>
                    )}
                  </label>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>Optionale Angaben</div>
                  <button type="button" className="btn" onClick={() => setShowAdvanced((v) => !v)}>{showAdvanced ? 'Weniger anzeigen' : 'Mehr anzeigen'}</button>
                </div>
                {showAdvanced && (
                  <>
                    <div className="admin-form form-row">
                      <label>
                        Ansprechpartner
                        <input value={form.details_contactPerson} onChange={(e) => setForm({ ...form, details_contactPerson: e.target.value })} />
                      </label>
                      <label>
                        Ansprechpartner Telefon
                        <input value={form.details_contactPhone} onChange={(e) => setForm({ ...form, details_contactPhone: e.target.value })} />
                      </label>
                      <label>
                        Auftraggeber
                        <input value={form.details_client} onChange={(e) => setForm({ ...form, details_client: e.target.value })} />
                      </label>
                      <label>
                        Leistungen
                        <input value={form.details_services} onChange={(e) => setForm({ ...form, details_services: e.target.value })} />
                      </label>
                      <label>
                        Fläche
                        <input value={form.details_area} onChange={(e) => setForm({ ...form, details_area: e.target.value })} />
                      </label>
                      <label>
                        Projektvolumen
                        <input value={form.details_budget} onChange={(e) => setForm({ ...form, details_budget: e.target.value })} />
                      </label>
                      <label>
                        Projektdauer
                        <input value={form.details_duration} onChange={(e) => setForm({ ...form, details_duration: e.target.value })} />
                      </label>
                    </div>
                    <div className="admin-form form-row">
                      <label>
                        Leistungszeitraum
                        <input value={form.details_servicePeriod} onChange={(e) => setForm({ ...form, details_servicePeriod: e.target.value })} placeholder="12/2017 bis 03/2023" />
                      </label>
                      <label>
                        Leistungsphasen (LPH)
                        <input value={form.details_performancePhases} onChange={(e) => setForm({ ...form, details_performancePhases: e.target.value })} placeholder="LPH 1-3 und 5-9" />
                      </label>
                      <label>
                        Anlagengruppen
                        <input value={form.details_plantGroups} onChange={(e) => setForm({ ...form, details_plantGroups: e.target.value })} placeholder="4, 5 und 6 (ELT)" />
                      </label>
                    </div>
                    <label>
                      Planungsmerkmale (eine pro Zeile)
                      <textarea value={form.details_planningFeatures} onChange={(e) => setForm({ ...form, details_planningFeatures: e.target.value })} placeholder={'Planung in 3D zur Kollisionsprüfung\n...'} />
                    </label>
                  </>
                )}
                {submitError && (
                  <div
                    className="error-text"
                    style={{ color: 'var(--danger-color)', marginBottom: 8 }}
                  >
                    {submitError}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn" type="button" onClick={() => setIsAddOpen(false)}>Abbrechen</button>
                  <button className="btn btn-primary" disabled={isSubmitting} type="submit">{isSubmitting ? (uploadProgress > 0 && uploadProgress < 100 ? `Upload ${uploadProgress}%…` : 'Speichern…') : 'Speichern'}</button>
                </div>
              </form>
            
          </div>
        </div>
      </div> */}
      
      {/* Admin-Editor vollständig entfernt */}

      {/* Reference Modal */}
      <div className={`reference-modal ${modalOpen ? 'open' : ''}`} onClick={closeModal}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          {selectedReference && (
            <>
              <button className="modal-close" onClick={closeModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <div className="modal-header">
                <h2 className="modal-title">{selectedReference.title}</h2>
                <div className="modal-subtitle">
                  <span>
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-1" /> {selectedReference.location}
                  </span>
                  <span>{selectedReference.year}</span>
                </div>
              </div>
              <div className="modal-body">
                <div className="modal-image">
                  {selectedReference.media && selectedReference.media.length > 0 ? (
                    selectedReference.media[currentMediaIndex]?.type === 'video' ? (
                      <video controls style={{ width: '100%', borderRadius: 8 }}>
                        <source src={normalizeDriveUrl(selectedReference.media[currentMediaIndex]?.url, 'video')} />
                      </video>
                    ) : (
                      <>
                        <img
                          src={normalizeDriveUrl(selectedReference.media[currentMediaIndex]?.url, 'image')}
                          alt={selectedReference.title}
                          style={{ cursor: 'zoom-in' }}
                          onClick={() => {
                            setZoomUrl(normalizeDriveUrl(selectedReference.media?.[currentMediaIndex]?.url, 'image'));
                            setIsZoomOpen(true);
                          }}
                        />
                        {selectedReference.media.length > 1 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                            <button
                              className="btn"
                              onClick={() => setCurrentMediaIndex((prev) => (prev - 1 + (selectedReference.media?.length || 1)) % (selectedReference.media?.length || 1))}
                            >
                              Zurück
                            </button>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                              {currentMediaIndex + 1} / {selectedReference.media.length}
                            </div>
                            <button
                              className="btn"
                              onClick={() => setCurrentMediaIndex((prev) => ((prev + 1) % (selectedReference.media?.length || 1)))}
                            >
                              Weiter
                            </button>
                          </div>
                        )}
                      </>
                    )
                  ) : (
                    selectedReference.mediaType === 'video' && selectedReference.mediaUrl ? (
                      <video controls style={{ width: '100%', borderRadius: 8 }}>
                        <source src={normalizeDriveUrl(selectedReference.mediaUrl, 'video')} />
                      </video>
                    ) : (
                      selectedReference.image ? (
                        <img
                          src={normalizeDriveUrl((selectedReference.mediaUrl && selectedReference.mediaType === 'image') ? selectedReference.mediaUrl : selectedReference.image, 'image')}
                          alt={selectedReference.title}
                          style={{ cursor: 'zoom-in' }}
                          onClick={() => {
                            setZoomUrl(normalizeDriveUrl((selectedReference.mediaUrl && selectedReference.mediaType === 'image') ? selectedReference.mediaUrl : selectedReference.image, 'image'));
                            setIsZoomOpen(true);
                          }}
                        />
                      ) : (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          fontSize: '1.5rem',
                          fontWeight: '600',
                          textAlign: 'center',
                          padding: '60px 20px',
                          borderRadius: 8,
                          minHeight: '300px'
                        }}>
                          Bild folgt in Kürze
                        </div>
                      )
                    )
                  )}
                </div>
                <div className="modal-description">
                  <p>{selectedReference.description}</p>
                </div>
                <div className="modal-details">
                  <div className="detail-item">
                    <div className="detail-label">Auftraggeber:</div>
                    <div className="detail-value">{selectedReference.details.client}</div>
                  </div>
                  {(selectedReference.details.contactPerson || selectedReference.details.contactPhone) && (
                    <div className="detail-item">
                      <div className="detail-label">Ansprechpartner:</div>
                      <div className="detail-value">
                        {selectedReference.details.contactPerson}
                        {selectedReference.details.contactPhone ? ` (Tel.: ${selectedReference.details.contactPhone})` : ''}
                      </div>
                    </div>
                  )}
                  {selectedReference.details.constructionType && (
                    <div className="detail-item">
                      <div className="detail-label">Bauart:</div>
                      <div className="detail-value">
                        {[['Bauen im laufenden Betrieb', selectedReference.details.constructionType.imBetrieb], ['Neubau', selectedReference.details.constructionType.neubau], ['Erweiterungsbau', selectedReference.details.constructionType.erweiterung], ['Umbau', selectedReference.details.constructionType.umbau]]
                          .filter(([, v]) => v)
                          .map(([k]) => k)
                          .join(', ')}
                      </div>
                    </div>
                  )}
                  <div className="detail-item">
                    <div className="detail-label">Leistungen:</div>
                    <div className="detail-value">{selectedReference.details.services}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Fläche:</div>
                    <div className="detail-value">{selectedReference.details.area}</div>
                  </div>
                  {selectedReference.details.budget && (
                    <div className="detail-item">
                      <div className="detail-label">Projektvolumen:</div>
                      <div className="detail-value">{selectedReference.details.budget}</div>
                    </div>
                  )}
                  {selectedReference.details.servicePeriod && (
                    <div className="detail-item">
                      <div className="detail-label">Leistungszeitraum:</div>
                      <div className="detail-value">{selectedReference.details.servicePeriod}</div>
                    </div>
                  )}
                  <div className="detail-item">
                    <div className="detail-label">Projektdauer:</div>
                    <div className="detail-value">{selectedReference.details.duration}</div>
                  </div>
                  {selectedReference.details.performancePhases && (
                    <div className="detail-item">
                      <div className="detail-label">Leistungsphasen:</div>
                      <div className="detail-value">{selectedReference.details.performancePhases}</div>
                    </div>
                  )}
                  {selectedReference.details.plantGroups && (
                    <div className="detail-item">
                      <div className="detail-label">Anlagengruppen:</div>
                      <div className="detail-value">{selectedReference.details.plantGroups}</div>
                    </div>
                  )}
                  {selectedReference.details.planningFeatures && selectedReference.details.planningFeatures.length > 0 && (
                    <div className="detail-item">
                      <div className="detail-label">Planungsmerkmale:</div>
                      <div className="detail-value">
                        <ul style={{ paddingLeft: 18, margin: 0 }}>
                          {selectedReference.details.planningFeatures.map((f, i) => (
                            <li key={i}>{f}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {isZoomOpen && (
        <div className="image-zoom-overlay" onClick={() => setIsZoomOpen(false)}>
          <button
            className="image-zoom-close"
            onClick={() => setIsZoomOpen(false)}
            aria-label="Schließen"
          >
            ×
          </button>
          {zoomUrl && (
            <img
              src={zoomUrl}
              alt="Zoom"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ReferenzenPage;